// src/contexts/LocationContext.js
// PURPOSE : Provides master location definitions to the whole app.
//           e.g. dropdowns when assigning a location to an asset.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { locationService } from "../services/locationService";
import { useAuth }        from "./AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// Context & hook
// ─────────────────────────────────────────────────────────────────────────────
const LocationContext = createContext();

export const useLocations = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    throw new Error("[useLocations] must be used inside <LocationsProvider>.");
  }
  return ctx;
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────
export const LocationsProvider = ({ children }) => {
  const { currentUser } = useAuth();

  const [locations, setLocations] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // ── Fetch master locations ─────────────────────────────────────────────────
  const fetchLocations = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.info("[LocationContext] Fetching master locations...");
      const data = await locationService.getItems(currentUser);
      setLocations(data ?? []);
    } catch (err) {
      console.error("[LocationContext] fetchLocations failed:", err);
      setError(err.message || "Failed to load locations.");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // ── Create a new master location ───────────────────────────────────────────
  const createLocation = useCallback(
    async (locationData) => {
      if (!currentUser) {
        throw new Error("[LocationContext] No authenticated user.");
      }

      try {
        const created = await locationService.createItem(currentUser, {
          ...locationData,
          companyId: locationData.companyId || currentUser.companyId,
          company:   locationData.company   || currentUser.company || "",
        });

        // Optimistic update
        setLocations((prev) => [...prev, created]);

        return created;
      } catch (err) {
        console.error("[LocationContext] createLocation failed:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // ── Update a master location ───────────────────────────────────────────────
  const updateLocation = useCallback(
    async (locationId, updateData) => {
      if (!currentUser) {
        throw new Error("[LocationContext] No authenticated user.");
      }

      try {
        const updated = await locationService.updateItem(
          currentUser,
          locationId,
          {
            ...updateData,
            companyId: updateData.companyId || currentUser.companyId,
          }
        );

        // Optimistic update
        setLocations((prev) =>
          prev.map((loc) =>
            loc.id === locationId ? { ...loc, ...updated } : loc,
          ),
        );

        return updated;
      } catch (err) {
        console.error("[LocationContext] updateLocation failed:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // ── Delete a master location ───────────────────────────────────────────────
  const deleteLocation = useCallback(
    async (locationId) => {
      if (!currentUser) {
        throw new Error("[LocationContext] No authenticated user.");
      }

      try {
        await locationService.deleteItem(locationId);

        // Optimistic removal
        setLocations((prev) => prev.filter((loc) => loc.id !== locationId));

        return { success: true };
      } catch (err) {
        console.error("[LocationContext] deleteLocation failed:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // ── Manual refresh ─────────────────────────────────────────────────────────
  const refreshLocations = useCallback(async () => {
    await fetchLocations();
  }, [fetchLocations]);

  // ── Context value ──────────────────────────────────────────────────────────
  const value = {
    locations,
    loading,
    error,
    createLocation,
    updateLocation,
    deleteLocation,
    refreshLocations,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};